import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, MessageSquare, Trash2, Bot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PAGE_LABELS = {
  '/dashboard': 'لوحة التحكم',
  '/invoices': 'الفواتير',
  '/products': 'المنتجات',
  '/customers': 'العملاء',
  '/suppliers': 'الموردين',
  '/employees': 'الموظفين',
  '/hr/leaves': 'الإجازات',
  '/expenses': 'المصروفات',
  '/purchase-orders': 'أوامر الشراء',
  '/reports': 'التقارير',
};

export default function FloatingAIAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Reset conversation when page changes
  useEffect(() => {
    setMessages([]);
    setIsOpen(false);
  }, [location.pathname]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const conversationHistory = messages.map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_BASE}/api/ai/context-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          message: userMessage,
          current_page: location.pathname,
          conversation_history: conversationHistory,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.answer || 'لا توجد إجابة',
          suggestedActions: Array.isArray(data.suggested_actions) ? data.suggested_actions : [],
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'عذراً، حدث خطأ في الاتصال.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'تعذر الاتصال بالمساعد الذكي.' }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, location.pathname]);

  const handleAction = (action) => {
    if (action.action === 'navigate' && action.params?.url) {
      navigate(action.params.url);
      setIsOpen(false);
    }
    // Other actions can be handled by page components via events
  };

  if (!user) return null;

  const pageLabel = PAGE_LABELS[location.pathname] || 'الصفحة الحالية';

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col items-end gap-3" dir="rtl">
      {/* Chat Panel */}
      {isOpen && (
        <div className="w-80 h-[480px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-semibold">مساعد {pageLabel}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMessages([])}
                className="p-1 text-white/70 hover:text-white transition-colors"
                title="مسح المحادثة"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-white/70 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Bot className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">اسألني أي سؤال عن بيانات هذه الصفحة</p>
                <div className="mt-3 space-y-1">
                  {Object.entries({
                    '/invoices': ['أرني الفواتير المتأخرة', 'ما إجمالي المبيعات؟'],
                    '/products': ['أي منتجات تنفد؟', 'أكثر المنتجات مبيعاً'],
                    '/dashboard': ['ما أهم 3 مشاكل اليوم؟', 'ملخص الأداء'],
                    '/employees': ['لخّص الأداء', 'من طلب إجازة؟'],
                  })[location.pathname]?.map((hint, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(hint); setTimeout(() => inputRef.current?.focus(), 100); }}
                      className="block w-full text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-1.5 transition-colors text-right"
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}>
                  <p className="leading-relaxed">{msg.content}</p>
                  {msg.role === 'assistant' && Array.isArray(msg.suggestedActions) && msg.suggestedActions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.suggestedActions.map((action, j) => (
                        <button
                          key={j}
                          onClick={() => handleAction(action)}
                          className="text-xs bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50 rounded-full px-2 py-1 transition-colors"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    {[0,1,2].map(i => (
                      <div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: `${i * 0.15}s`}} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-3 flex gap-2 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="اكتب سؤالك..."
              className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-right"
              dir="rtl"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="w-9 h-9 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isOpen
            ? 'bg-gray-700 hover:bg-gray-800'
            : 'bg-gradient-to-br from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700'
        }`}
      >
        {isOpen
          ? <X className="w-6 h-6 text-white" />
          : <Sparkles className="w-6 h-6 text-white" />
        }
      </button>
    </div>
  );
}
